/// <amd-module name="Wit/Common/Controls/Boards/Card" />

import { IObservableValue, ObservableValue, useObservable } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { localeDateFormat } from "VSS/Platform/Date";
import { css, useComponentContext } from "VSS/Platform/Layout";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { Portal } from "VSSUI/Portal";
import { Spinner } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { Toggle } from "VSSUI/Toggle";
import { Tooltip } from "VSSUI/TooltipEx";
import { KeyCode } from "VSSUI/Util";
import { hexToRgb, isDark } from "VSSUI/Utilities/Color";
import { WorkItemRollupLabel, WorkItemRollupProgressBar } from "Wit/AgileAnalytics/Components/WorkItemRollupProgressBar";
import { IWorkItemRollupProvider, RollupProgressBarData } from "Wit/AgileAnalytics/WorkItemRollupProvider";
import { IClientWorkItem, IObservableClientWorkItem, ITitleStyle, ParentField } from "Wit/ClientWorkItemService/IClientWorkItem";
import * as Resources from "Wit/Common/Controls/Boards/Resources";
import { getDateInUserTimeZone } from "Wit/Common/DateUtils";
import { CoreFieldRefNames, FieldType, OobFieldRefNames, WorkItemTypes } from "Wit/Common/Generated/Constants";
import { IHotKeyEventData, IHotKeyEventService, IHotKeyEventType } from "Wit/Common/HotKeyEventService/IHotKeyEventService";
import { formatRemainingWorkForDisplay, getNumberInUserFormat } from "Wit/Common/Utilities/FormatUtils";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { getAreaPathLeafNode } from "Wit/Filter/AreaPathHelper";
import { WorkItemIdentityRef } from "Wit/Identity/Interfaces/WorkItemInterfaces";
import { convertWorkItemIdentityRefFromFieldValue } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { parseColor } from "Wit/UI/Common/ColorUtilities";
import { IdentityDisplayMode, IdentityView } from "Wit/UI/IdentityView";
import { IWorkItemStateColorMetadataService, WorkItemStateColorMetadataServiceId } from "Wit/UI/Services/WorkItemStateColorMetadataService";
import {
    IWorkItemTypeIconMetadataService,
    WorkItemTypeIconMetadataServiceId,
    getIconDataObservable
} from "Wit/UI/Services/WorkItemTypeIconMetadataService";
import { TagsList } from "Wit/UI/TagsList";
import { WorkItemState } from "Wit/UI/WorkItemState";
import { TypeIconSize, WorkItemTypeColorAndIcon, WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";
import * as React from "react";
import { useState } from "react";
import { useBacklogManagementPermission } from "./AgileBacklogPermissionContext";
import { DragMode, IWrappedDraggableProps, MakeDraggable } from "./Draggable";
import { EditableField, OnChangeType } from "./EditableField";
import { CoreFields, FieldLayout, FieldLayoutMap, WorkItemLayout } from "./Interfaces";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

interface ICommonCardProps<TWorkItem extends IClientWorkItem, TObservableWorkItem extends IObservableClientWorkItem<TWorkItem>> {
    /** Only show the title */
    cardFieldsCollapsed: boolean;
    isNew?: boolean;
    itemLayout: WorkItemLayout<TWorkItem, TObservableWorkItem>;
    showEmptyFields: boolean;
    showChildRollup: boolean;
    /** Make the card small, don't display fields or tags. */
    compact: boolean;
    /** Always display all fields. */
    full: boolean;
    /** renders additional content inside the card */
    onRenderAdditionalContent?: (item: TObservableWorkItem) => JSX.Element;
    onRenderMenuButton?: (pageContext: IVssPageContext, item: TObservableWorkItem) => JSX.Element;
    onCardClick?: (e: React.SyntheticEvent<HTMLElement>, workItemId: number) => void;
    onChange?: OnChangeType;
    onNewItemCancel?: () => void;
    onNewItemCreate?: (newTitle: string, enterPressed: boolean) => void;
    /** Called when the user clicks on a work item title (e.g. the title of the card or the parent work item) */
    onWorkItemClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLAnchorElement>, workItemId: number) => void;
    remainingWorkField?: string;
    remainingWorkText?: string;
    rollupProviders?: IWorkItemRollupProvider[];
    /** Mapping of tag names to colors */
    tagStyles?: Map<string, string>;
    wrapTags?: boolean;
    workItemType?: WorkItemTypes;
    onDraggableMove?: (e: PointerEvent, scrollLeftValue: number, scrollTopValue: number) => void;
    onFocus?: (event: React.FocusEvent<HTMLElement>) => void;
    canChangeRemainingWork?: boolean;
}

export interface IDraggableCardProps<TWorkItem extends IClientWorkItem, TObservableWorkItem extends IObservableClientWorkItem<TWorkItem>>
    extends ICommonCardProps<TWorkItem, TObservableWorkItem> {
    className?: string;
    /** Used for grid layout, leave undefined for flex layout */
    row?: number;
    /** Used for grid layout, leave undefined for flex layout */
    startColumn?: string | number;
    /** Used for grid layout, leave undefined for flex layout */
    endColumn?: string | number;
    calloutContent?: string;
    /**
     * Props to add as data attributes to the card element.
     */
    dataProps?: { [key: `data-${string}`]: string | undefined };
    isTitleEditing?: IObservableValue<boolean>;
    onKeyDown?: (event: React.KeyboardEvent<HTMLElement>, item: TObservableWorkItem) => void;
    onFocus?: (event: React.FocusEvent<HTMLElement>) => void;
    disableEmptyFieldsToggle?: Boolean;
    columnName?: string;
    columnIsSplited?: boolean;
    columnIsDone?: boolean;
    rowName?: string;
    columIsSplited?: boolean;
    columnChanged?: ObservableValue<boolean>;
    doneChanged?: ObservableValue<boolean>;
    rowChanged?: ObservableValue<boolean>;
}

const nonEditableCardFields = new Set([
    CoreFieldRefNames.AreaId,
    CoreFieldRefNames.AttachedFileCount,
    CoreFieldRefNames.AuthorizedAs,
    CoreFieldRefNames.AuthorizedDate,
    CoreFieldRefNames.BoardColumn,
    CoreFieldRefNames.BoardColumnDone,
    CoreFieldRefNames.BoardLane,
    CoreFieldRefNames.ChangedBy,
    CoreFieldRefNames.CreatedBy,
    CoreFieldRefNames.CreatedDate,
    CoreFieldRefNames.ExternalLinkCount,
    CoreFieldRefNames.History,
    CoreFieldRefNames.HyperLinkCount,
    CoreFieldRefNames.Id,
    CoreFieldRefNames.IterationId,
    CoreFieldRefNames.LinkType,
    CoreFieldRefNames.NodeName,
    CoreFieldRefNames.Reason,
    CoreFieldRefNames.RelatedLinkCount,
    CoreFieldRefNames.Rev,
    CoreFieldRefNames.RevisedDate,
    CoreFieldRefNames.TeamProject,
    CoreFieldRefNames.Watermark,
    CoreFieldRefNames.WorkItemType,
    CoreFieldRefNames.IsDeleted,
    OobFieldRefNames.BacklogPriority,
    OobFieldRefNames.StackRank,
    OobFieldRefNames.ActivatedBy,
    OobFieldRefNames.ActivatedDate,
    OobFieldRefNames.ClosedBy,
    OobFieldRefNames.ClosedDate,
    OobFieldRefNames.StateChangeDate,
    "System.PersonId"
]);

/** renders a card on the board and, while a drag is underway, the card being dragged */
function DraggableCardImpl<TWorkItem extends IClientWorkItem, TObservableWorkItem extends IObservableClientWorkItem<TWorkItem>>(
    props: IDraggableCardProps<TWorkItem, TObservableWorkItem> & IWrappedDraggableProps
) {
    return (
        <>
            {/* it's important to strip out the position prop though so that it's not changing and ruining the memoization */}
            <CardMemo
                {...props}
                className={css(props.dragging && "ghost", props.className)}
                dragging={false}
                dragMode={DragMode.move}
                position={undefined}
                calloutContent={undefined}
            />
            {/* we don't use the memoized version for the dragging card because its props change so much that caching is pointless */}
            {!props.isMoveDisabled?.() && props.dragging && (
                <Portal className="delivery-plans-portal">
                    <Card {...props} />
                </Portal>
            )}
        </>
    );
}

const DraggableUnmemodCard = MakeDraggable(DraggableCardImpl);
/** a draggable (and droppable) Card */
// React.memo loses the generic typing, so we have to cast it back
export const DraggableCard = React.memo(DraggableUnmemodCard) as typeof DraggableUnmemodCard;

export namespace CardConstants {
    export const cardClassName = "wit-card";
}

interface ICardProps<TWorkItem extends IClientWorkItem, TObservableWorkItem extends IObservableClientWorkItem<TWorkItem>>
    extends IDraggableCardProps<TWorkItem, TObservableWorkItem>,
        IWrappedDraggableProps {
    className?: string;
}

const Card = React.forwardRef<HTMLDivElement, ICardProps<IClientWorkItem, IObservableClientWorkItem>>((props, ref) => {
    const {
        resizeDisabled,
        cardFieldsCollapsed,
        compact,
        dragging,
        itemLayout,
        position,
        onKeyDown,
        remainingWorkField,
        remainingWorkText,
        columnName,
        onFocus,
        columnChanged,
        canChangeRemainingWork
    } = props;
    const hasBacklogManagementPermission = useBacklogManagementPermission();

    const style: React.CSSProperties = dragging
        ? {
              position: "absolute",
              left: position?.left,
              top: position?.top,
              width: position?.width,
              height: position?.height
          }
        : {
              gridColumnStart: props.startColumn,
              gridColumnEnd: props.endColumn,
              gridRow: props.row
          };

    const onPointerDown = React.useCallback(
        (e: React.PointerEvent<HTMLElement>) => {
            if ((e.target as HTMLElement).classList.contains("card-resizable-left")) {
                props.onPointerDown(e, DragMode.resizeLeft);
            } else if ((e.target as HTMLElement).classList.contains("card-right-resize-handle")) {
                props.onPointerDown(e, DragMode.resizeRight);
            } else {
                props.onPointerDown(e, DragMode.move);
            }
        },
        [props.onPointerDown]
    );
    const onKeyDownHandler = React.useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            if (!event.defaultPrevented && event.which === KeyCode.enter) {
                props.onCardClick && props.onCardClick(event, itemLayout.workItem.value.id);
                event.preventDefault();
            }

            onKeyDown?.(event, itemLayout.workItem);
        },
        [props.onCardClick, onKeyDown, itemLayout]
    );
    const onFocusHandler = (event: React.FocusEvent<HTMLElement>) => {
        onFocus?.(event);
    };
    const onTitleEditComplete = React.useCallback(() => {
        if (props.isTitleEditing) {
            props.isTitleEditing.value = false;
        }
    }, [props.isTitleEditing]);

    const { pageContext } = useComponentContext();
    const assignedToFieldValue = itemLayout.workItem.value.getFieldValue<string | WorkItemIdentityRef>(CoreFieldRefNames.AssignedTo);
    const assignedTo =
        CoreFieldRefNames.AssignedTo in itemLayout.coreFields &&
        assignedToFieldValue &&
        convertWorkItemIdentityRefFromFieldValue(pageContext, assignedToFieldValue).identityRef;

    let assignedToDescription: string;
    if (assignedTo) {
        assignedToDescription = format(Resources.CardAssignedToAriaLabel, assignedTo.displayName);
    } else {
        assignedToDescription = Resources.CardUnassignedAriaLabel;
    }

    const typeName = itemLayout.workItem.value.getFieldValue<string>(CoreFieldRefNames.WorkItemType);
    const title = itemLayout.workItem.value.getFieldValue<string>(CoreFieldRefNames.Title);
    const id = itemLayout.workItem.value.id;

    const cardDescription = format(Resources.CardBaseAriaLabel, typeName, title, id, assignedToDescription);

    const getAriaLabel = (columnChanged: boolean | undefined, doneChanged: boolean | undefined, rowChanged: boolean | undefined) => {
        const columnFullName =
            props.columnIsSplited && props.columnIsSplited === true
                ? props.columnIsDone && props.columnIsDone === true
                    ? format(Resources.CardDoneColumn, columnName)
                    : format(Resources.CardDoingColumn, columnName)
                : columnName;

        const columnCardDescription = columnFullName ? format(Resources.CardWithColumnAriaLabel, columnFullName, cardDescription) : cardDescription;

        const columnResult = columnChanged || doneChanged ? columnCardDescription : cardDescription;

        const result =
            rowChanged && rowChanged === true && props.rowName ? format(Resources.CardWithRowAriaLabel, props.rowName, columnResult) : columnResult;
        return result;
    };

    return (
        <Observer
            item={itemLayout.workItem}
            isTitleEditing={props.isTitleEditing}
            columnChanged={columnChanged}
            doneChanged={props.doneChanged}
            rowChanged={props.rowChanged}
        >
            {({ item, isTitleEditing, columnChanged, doneChanged, rowChanged }) => (
                <div
                    data-itemid={item.id}
                    aria-label={getAriaLabel(columnChanged, doneChanged, rowChanged)}
                    role="group"
                    aria-roledescription={"Card"}
                    {...props.dataProps}
                    className={css(CardConstants.cardClassName, "depth-8 flex-row", compact && "compact", dragging && "dragging", props.className)}
                    onKeyDown={item.saving ? undefined : onKeyDownHandler}
                    onPointerDown={item.saving ? undefined : onPointerDown}
                    onFocus={item.saving ? undefined : onFocusHandler}
                    ref={ref}
                    style={{ ...style, backgroundColor: item.backgroundColor }}
                    tabIndex={0}
                >
                    <div
                        className={css("card-flag flex-noshrink", !resizeDisabled && "card-resizable-left")}
                        data-side="left"
                        style={{ backgroundColor: parseColor(itemLayout.workItemTypeColorAndIcon.color) }}
                    />
                    <CardContentMemo
                        canEdit={hasBacklogManagementPermission}
                        cardFieldsCollapsed={cardFieldsCollapsed}
                        compact={props.compact}
                        dragging={dragging}
                        isNew={props.isNew}
                        isTitleEditing={isTitleEditing}
                        item={item}
                        itemId={item.id}
                        itemLayout={itemLayout}
                        full={props.full}
                        parentId={item.getFieldValue(CoreFieldRefNames.Parent) as number}
                        onCardClick={props.onCardClick}
                        onChange={props.onChange}
                        onNewItemCancel={props.onNewItemCancel}
                        onNewItemCreate={props.onNewItemCreate}
                        onWorkItemClick={props.onWorkItemClick}
                        onRenderAdditionalContent={props.onRenderAdditionalContent}
                        onRenderMenuButton={props.onRenderMenuButton}
                        onTitleEditComplete={onTitleEditComplete}
                        remainingWorkField={remainingWorkField}
                        remainingWorkText={remainingWorkText}
                        resizeDisabled={resizeDisabled}
                        rev={itemLayout.workItem.rev}
                        rollupProviders={props.rollupProviders}
                        showChildRollup={props.showChildRollup}
                        showEmptyFields={props.showEmptyFields}
                        tagStyles={props.tagStyles}
                        titleStyle={item.titleStyle}
                        wrapTags={props.wrapTags}
                        workItemType={props.workItemType}
                        disableEmptyFieldsToggle={props.disableEmptyFieldsToggle}
                        canChangeRemainingWork={canChangeRemainingWork}
                    />
                    {!resizeDisabled && <div className="card-right-resize-handle flex-noshrink" data-side="right" />}
                    {item.saving && <CardSavingOverlay />}
                    {props.calloutContent && (
                        <div
                            className={css(
                                "edge-drag-callout-container flex-column justify-center",
                                props.dragMode === DragMode.resizeLeft ? "left" : "right"
                            )}
                        >
                            <div className="edge-drag-callout">{props.calloutContent}</div>
                        </div>
                    )}
                </div>
            )}
        </Observer>
    );
});

const CardMemo = React.memo(Card);

interface ICardContentProps<TWorkItem extends IClientWorkItem, TObservableWorkItem extends IObservableClientWorkItem<TWorkItem>>
    extends ICommonCardProps<TWorkItem, TObservableWorkItem> {
    canEdit: boolean;
    dragging: boolean;
    isNew?: boolean;
    isTitleEditing?: boolean;
    item: TWorkItem;
    onTitleEditComplete?: () => void;
    resizeDisabled?: boolean;
    /** item id so we can break memo when an ID is assigned following the initial save */
    itemId: number;
    /** parent id so we can break memo when parent changes */
    parentId?: number;
    /** work item revision so we can break memo when fields change */
    rev: number;
    titleStyle: ITitleStyle | undefined;
    disableEmptyFieldsToggle?: Boolean;
}

function CardContent<TWorkItem extends IClientWorkItem, TObservableWorkItem extends IObservableClientWorkItem<TWorkItem>>(
    props: ICardContentProps<TWorkItem, TObservableWorkItem>
): JSX.Element {
    const { item, itemLayout, isTitleEditing, remainingWorkField, remainingWorkText, workItemType, canChangeRemainingWork } = props;
    const [emptyFieldsShown, setEmptyFieldsShown] = useState(false);

    const { pageContext } = useComponentContext();
    const [newTitle, setNewTitle] = useObservable<string>("");
    const additionalFields: FieldLayout[] = [...itemLayout.additionalFields];
    let remainingWork = remainingWorkText;

    if (remainingWorkField && !remainingWorkText) {
        const remainingWorkIndex = additionalFields.findIndex(field => field.referenceName === remainingWorkField);
        if (remainingWorkIndex >= 0) {
            // remove from the list of fields to show since we will be showing this in a custom way instead.
            additionalFields.splice(remainingWorkIndex, 1);
        }
        let remainingWorkValue = item.getFieldValue<number>(remainingWorkField);
        if (remainingWorkValue) {
            remainingWork = formatRemainingWorkForDisplay(remainingWorkValue);
        }
    }

    const toggleEmptyFields = React.useCallback(() => {
        setEmptyFieldsShown(emptyFieldsShown => !emptyFieldsShown);
    }, [emptyFieldsShown]);
    
    const isAllowedWorkItemType = canChangeRemainingWork && remainingWorkField;
    const showRemainingHours = itemLayout.additionalFields.some(field => field.referenceName === OobFieldRefNames.RemainingWork) || (!isAllowedWorkItemType  && remainingWork);

    const hotKeyEventService = pageContext.getService<IHotKeyEventService>("IHotKeyEventService");

    const workItemToggleEmptyFieldsCallback = ({ workItemId }: IHotKeyEventData) => {
        if (!props.showEmptyFields && itemLayout.workItem.value?.id == workItemId) {
            toggleEmptyFields();
        }
    };

    React.useEffect(() => {
        hotKeyEventService.subscribe(workItemToggleEmptyFieldsCallback, IHotKeyEventType.WorkItemToggleEmptyFields);
        return () => hotKeyEventService.unsubscribe(workItemToggleEmptyFieldsCallback, IHotKeyEventType.WorkItemToggleEmptyFields);
    }, []);

    const workItemState = itemLayout.coreFields[CoreFieldRefNames.State];
    const parent = itemLayout.coreFields[CoreFieldRefNames.Parent];
    if (parent !== undefined) {
        additionalFields.unshift(parent);
    }

    const editableFieldBorderColor = item.backgroundColor
        ? isDark(hexToRgb(item.backgroundColor))
            ? css("light-border")
            : css("dark-border")
        : undefined;

    const assignedToFieldValue = item.getFieldValue<string | WorkItemIdentityRef>(CoreFieldRefNames.AssignedTo);

    const assignedTo =
        CoreFieldRefNames.AssignedTo in itemLayout.coreFields &&
        (props.showEmptyFields || emptyFieldsShown || assignedToFieldValue) &&
        convertWorkItemIdentityRefFromFieldValue(
            pageContext,
            assignedToFieldValue // with the new DataProvider, this will be WorkItemIdentityRef all the time I think
        ).identityRef;
    const assignedToView = assignedTo ? (
        <IdentityView
            displayTooltipOnHover={true}
            identityRef={assignedTo}
            className="text-ellipsis card-assigned-to"
            viewMode={IdentityDisplayMode.AvatarAndName}
            size="extra-small-plus"
        />
    ) : undefined;
    const tags = CoreFieldRefNames.Tags in itemLayout.coreFields && item.getFieldValue<string>(CoreFieldRefNames.Tags)?.split("; ");

    const workItemFieldService =  isFeatureFlagEnabled(pageContext, FeatureFlags.CardsUseNewWorkItemFieldService, false) ?
              pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2")
            : pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");

    const fields =
        !props.compact &&
        renderFields(
            props.canEdit,
            pageContext,
            additionalFields,
            item,
            props.showEmptyFields || emptyFieldsShown,
            props.onChange,
            props.onWorkItemClick,
            workItemFieldService            
        );

    const hasEmptyFields =
        additionalFields.some(field => {
            const value =
                field.referenceName !== CoreFieldRefNames.Parent ? item.getFieldValue(field.referenceName) : item.getFieldValue(ParentField.Title);
            return !value;
        }) || !assignedToFieldValue;

    const showId = CoreFieldRefNames.Id in itemLayout.coreFields && item.id > 0;
    const title = item.getFieldValue<string>(CoreFieldRefNames.Title);
    const titleStyles = css(
        props.titleStyle?.isTitleBold && "font-weight-heavy",
        props.titleStyle?.isTitleItalicized && "font-italicized",
        props.titleStyle?.isTitleUnderlined && "text-underline"
    );
    const textColor = item.backgroundColor ? (isDark(hexToRgb(item.backgroundColor)) ? css("dark") : css("light")) : undefined;
    let titleContents;
    if (props.isNew) {
        titleContents = (
            <TextField
                autoFocus
                onBlur={() => props.onNewItemCreate && props.onNewItemCreate(newTitle.value, false)}
                onKeyDown={event => {
                    if (event.which === KeyCode.enter) {
                        props.onNewItemCreate && props.onNewItemCreate(newTitle.value, true);
                    } else if (event.which === KeyCode.escape) props.onNewItemCancel && props.onNewItemCancel();
                }}
                value={newTitle}
                onChange={(event, newValue) => {
                    setNewTitle(newValue);
                }}
                containerClassName="full-width"
            />
        );
    } else if (isTitleEditing) {
        titleContents = (
            <EditableField
                autoSelect
                fieldReferenceName={CoreFieldRefNames.Title}
                item={item}
                onChange={props.onChange}
                onEditCompleted={props.onTitleEditComplete}
                pageContext={pageContext}
                startInEditingMode={true}
            >
                <Tooltip overflowOnly>
                    <div className="text-ellipsis">{title}</div>
                </Tooltip>
            </EditableField>
        );
    } else {
        titleContents = (
            <Link
                className={css("title rhythm-horizontal-8", !props.resizeDisabled && "text-ellipsis")}
                draggable={false}
                href={itemLayout.url}
                onClick={e => props.onWorkItemClick(e, item.id)}
                tooltipProps={
                    !props.resizeDisabled
                        ? {
                              text: `${showId ? item.id + " " : ""}${title}`
                          }
                        : undefined
                }
                tabIndex={-1}
                removeUnderline={true}
            >
                <WorkItemTypeIcon data={itemLayout.workItemTypeColorAndIcon} suppressTooltip />
                {showId && <span className="font-weight-semibold">{item.id}</span>}
                <span
                    className={css("title-text", props.resizeDisabled ? "word-break" : "text-ellipsis", titleStyles)}
                    style={{ color: props.titleStyle?.titleColor }}
                >
                    {title}
                </span>
            </Link>
        );
    }

    let remainingWorkContent = undefined;
    if (showRemainingHours) {
        remainingWorkContent = (
            <Tooltip overflowOnly>
                <div className="text-ellipsis">{remainingWork?.toString()}</div>
            </Tooltip>
        );
    }

    return (
        <div
            className={css("card-content flex-column flex-grow", !props.resizeDisabled && "card-resizable-right", textColor)}
            onClick={e => props.onCardClick && props.onCardClick(e, item.id)}
        >
            <span className="flex-row full-width">
                {titleContents}
                {props.onRenderMenuButton && props.onRenderMenuButton(pageContext, itemLayout.workItem)}
            </span>
            {!props.cardFieldsCollapsed && (
                <div className="flex-column scroll-hidden">
                    <div className="flex-column">
                        {workItemState && (
                            <div className="flex-row flex-grow work-item-state-container text-ellipsis">
                                {renderState(pageContext, item, item.getFieldValue<string>(CoreFieldRefNames.State), props.onChange, props.canEdit)}
                            </div>
                        )}
                        <div className="flex-row">
                            {assignedToView &&
                                (props.canEdit ? (
                                    <EditableField
                                        autoSelect
                                        className={css("editable-card-identity-picker", "full-width padding-right-4", editableFieldBorderColor)}
                                        fieldReferenceName={CoreFieldRefNames.AssignedTo}
                                        isIdentity={true}
                                        item={item}
                                        onChange={props.onChange}
                                        pageContext={pageContext}
                                    >
                                        {assignedToView}
                                    </EditableField>
                                ) : (
                                    assignedToView
                                ))}
                            {showRemainingHours && (
                                <div className="remaining-work white-space-nowrap font-weight-semibold flex-self-end remaining-field-always-shown">
                                    {props.canEdit && (workItemType ? isAllowedWorkItemType : !remainingWorkText) ? (
                                        <EditableField
                                            autoSelect
                                            className="padding-right-4 padding-bottom-4"
                                            fieldReferenceName={remainingWorkField!}
                                            isIdentity={false}
                                            item={item}
                                            onChange={props.onChange}
                                            pageContext={pageContext}
                                            inputType="text"
                                        >
                                            {remainingWorkContent!}
                                        </EditableField>
                                    ) : (
                                        <>{remainingWorkContent}</>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    {!props.compact && (
                        <>
                            {fields && fields.length > 0 && <div className="flex-row flex-wrap fields">{fields}</div>}
                            {tags && tags.length > 0 && (
                                <TagsList className="margin-top-8" tags={tags} tagStyles={props.tagStyles} wrapTags={props.wrapTags} />
                            )}
                        </>
                    )}
                    {props.rollupProviders && props.showChildRollup && (
                        <WorkItemRollups compact={props.compact} item={item} pageContext={pageContext} rollupProviders={props.rollupProviders} />
                    )}
                    {!props.disableEmptyFieldsToggle && hasEmptyFields && !props.showEmptyFields && (
                        <div
                            className={css(
                                "card-expand-button-container",
                                emptyFieldsShown ? "bowtie-icon bowtie-chevron-up" : "bowtie-icon bowtie-chevron-down"
                            )}
                            onClick={() => {
                                toggleEmptyFields();
                            }}
                        />
                    )}
                </div>
            )}
            {props.onRenderAdditionalContent && props.onRenderAdditionalContent(itemLayout.workItem)}
        </div>
    );
}

const CardContentMemo = React.memo(CardContent);

function renderFields<TWorkItem extends IClientWorkItem>(
    canEdit: boolean,
    context: IVssPageContext,
    fields: FieldLayout[],
    item: TWorkItem,
    showEmptyFields: boolean,
    onChange: OnChangeType | undefined,
    onWorkItemClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLAnchorElement>, workItemId: number) => void,
    workItemFieldService: IWorkItemFieldService2 | IWorkItemFieldService) {
    const labelColor = item.backgroundColor
        ? isDark(hexToRgb(item.backgroundColor))
            ? css("label-light-secondary")
            : css("label-dark-secondary")
        : undefined;
    const editableFieldBorderColor = item.backgroundColor
        ? isDark(hexToRgb(item.backgroundColor))
            ? css("light-border")
            : css("dark-border")
        : undefined;

    return fields
        .map((field, index) => {
            let className: string | undefined = undefined;
            const value =
                field.referenceName !== CoreFieldRefNames.Parent ? item.getFieldValue(field.referenceName) : item.getFieldValue(ParentField.Title);

            if (!showEmptyFields && (value === null || value === undefined || value === "")) {
                return;
            }

            // some fields have custom renderers
            let valueContent: JSX.Element | undefined = undefined;
            if (field.referenceName === CoreFieldRefNames.State) {
                className = "value flex-row";
                valueContent = renderState(context, item, value as string);
            } else if (field.referenceName === CoreFieldRefNames.Parent) {
                className = "value flex-row";
                valueContent = renderParent(context, item, value as string, onWorkItemClick);
            } else if ([CoreFieldRefNames.AreaPath, CoreFieldRefNames.IterationPath].includes(field.referenceName)) {
                className = "value";
                valueContent = (
                    <Tooltip overflowOnly>
                        <div className="text-ellipsis reverse-ellipsis">{getAreaPathLeafNode(value as string)}</div>
                    </Tooltip>
                );
            } else if (field.isIdentity) {
                const identityRef = convertWorkItemIdentityRefFromFieldValue(context, value as string | WorkItemIdentityRef).identityRef;
                if (identityRef || showEmptyFields) {
                    className = "value assigned-to flex-row";
                    valueContent = (
                        <IdentityView
                            displayTooltipOnHover
                            identityRef={identityRef}
                            className="assigned-to text-ellipsis"
                            viewMode={IdentityDisplayMode.AvatarAndName}
                            size="extra-small-plus"
                            suppressTooltip
                        />
                    );
                } else {
                    return;
                }
            }

            let fieldTypeDescription = item.iWorkItem?.value?.getFieldByName(field.referenceName)?.fieldDefinition?.type;
            if (!fieldTypeDescription && workItemFieldService) {
                fieldTypeDescription = workItemFieldService.getFieldByReferenceName(field.referenceName)?.type;
            }
            // no content so far, render based on value type
            if (!valueContent) {
                let strValue: string | undefined = undefined;
                switch (typeof value) {
                    case "string":
                        strValue = value;
                        break;
                    case "number":
                        if (fieldTypeDescription === FieldType.Double) {
                            strValue = getNumberInUserFormat(value as number);
                        } else {
                            strValue = String(value);
                        }
                        break;
                    case "boolean":
                        strValue = String(value);
                        break;
                    case "undefined":
                        strValue = "";
                        break;
                    case "object":
                        if (value === null) {
                            strValue = "";
                        }
                        if (value instanceof Date) {
                            strValue = localeDateFormat(
                                getDateInUserTimeZone(value),
                                "d",
                                new Intl.DateTimeFormat(undefined, { year: "numeric", month: "numeric", day: "numeric" })
                            );
                            break;
                        }
                }
                if (strValue === undefined) {
                    console.warn("Unsupported value for field %O: %O", field.referenceName, value);
                    strValue = Resources.UnsupportedFieldType;
                }
                className = "value";
                valueContent = (
                    <Tooltip overflowOnly>
                        <div className="text-ellipsis">{strValue}</div>
                    </Tooltip>
                );
            }

            if (typeof value === "boolean" || fieldTypeDescription === FieldType.Boolean) {
                valueContent = renderToggle(context, item, field.referenceName, value as boolean, onChange);
            } else if (onChange && canEdit && !nonEditableCardFields.has(field.referenceName) && field.isEditable) {
                valueContent = (
                    <EditableField
                        autoSelect
                        className={css(className, editableFieldBorderColor)}
                        fieldReferenceName={field.referenceName}
                        isIdentity={field.isIdentity}
                        item={item}
                        onChange={onChange}
                        pageContext={context}
                    >
                        {valueContent}
                    </EditableField>
                );
            } else {
                valueContent = <div className={className}>{valueContent}</div>;
            }

            return (
                <div
                    className={css("field-container flex-row flex-grow flex-center text-ellipsis", index === 0 && "first-field")}
                    key={`${item.id}-${field.referenceName}`}
                >
                    <Tooltip overflowOnly>
                        <div className={css("label text-ellipsis", labelColor)}>{field.displayName}</div>
                    </Tooltip>
                    {valueContent}
                </div>
            );
        })
        .filter(f => f);
}

function renderParent<TWorkItem extends IClientWorkItem>(
    context: IVssPageContext,
    item: TWorkItem,
    parentTitle: string,
    onWorkItemClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLAnchorElement>, workItemId: number) => void
) {
    if (!parentTitle) {
        return;
    }

    const parentId = item.getFieldValue<number>(CoreFieldRefNames.Parent) ?? 0;
    const projectName = item.getFieldValue<string>(CoreFieldRefNames.TeamProject) ?? "";
    const workItemTypeName = item.getFieldValue<string>(ParentField.WorkItemType) ?? "";
    const colorAndIcon = getIconDataObservable(projectName, workItemTypeName, context);

    const linkTextColorStyle = item.backgroundColor ? (isDark(hexToRgb(item.backgroundColor)) ? css("light") : css("dark")) : undefined;
    return (
        <>
            <Link
                className={css("flex-row text-ellipsis", linkTextColorStyle)}
                onClick={e => {
                    onWorkItemClick(e, parentId);
                }}
                tabIndex={-1}
            >
                <WorkItemTypeIcon data={colorAndIcon} />
                <Tooltip overflowOnly>
                    <div className="value flex-grow text-ellipsis padding-left-4">{parentTitle}</div>
                </Tooltip>
            </Link>
        </>
    );
}

function renderState(
    context: IVssPageContext,
    item: IClientWorkItem,
    value = item.getFieldValue<string>(CoreFieldRefNames.State),
    onChange?: OnChangeType,
    canEdit?: boolean
) {
    const color = getWorkItemStateColor(context, item, value);
    const editableFieldBorderColor = item.backgroundColor
        ? isDark(hexToRgb(item.backgroundColor))
            ? css("light-border")
            : css("dark-border")
        : undefined;

    return (
        <Observer c={color}>
            {({ c }) =>
                canEdit ? (
                    <EditableField
                        allowFreeform={false}
                        autoSelect
                        className={css("card-work-item-state", editableFieldBorderColor)}
                        fieldReferenceName={CoreFieldRefNames.State}
                        item={item}
                        onChange={onChange}
                        pageContext={context}
                    >
                        <WorkItemState data={{ name: value!, fill: c }} />
                    </EditableField>
                ) : (
                    <WorkItemState data={{ name: value!, fill: c }} />
                )
            }
        </Observer>
    );
}

function renderToggle(
    context: IVssPageContext,
    item: IClientWorkItem,
    fieldReferenceName: string,
    value: boolean,
    onChange?: OnChangeType,
    canEdit?: boolean
) {
    return (
        <Toggle
            disabled={canEdit}
            checked={value}
            onChange={(ev, checked) => {
                onChange && onChange(context, item, fieldReferenceName, checked);
            }}
        />
    );
}

function getWorkItemStateColor(context: IVssPageContext, item: IClientWorkItem, state: string | null | undefined) {
    if (!state) {
        return "var(--palette-neutral-20)";
    }

    const colorService = context.getService<IWorkItemStateColorMetadataService>(WorkItemStateColorMetadataServiceId);
    const project = item.getFieldValue<string>(CoreFieldRefNames.TeamProject) ?? "";
    const type = item.getFieldValue<string>(CoreFieldRefNames.WorkItemType) ?? "";
    const color = colorService.getWorkItemStateColor(project, type, state);
    if (color instanceof Promise) {
        const observable = new ObservableValue("var(--palette-neutral-20)");
        color.then(c => {
            if (c.fill) {
                observable.value = "#" + c.fill;
            }
        });
        return observable;
    } else {
        return "#" + color.fill;
    }
}

function CardSavingOverlay() {
    return (
        <>
            <div className="absolute-fill card-saving-overlay sub-layer" />
            <div className="absolute-fill flex-column flex-center justify-center sub-layer">
                <Spinner />
            </div>
        </>
    );
}

interface RollupDisplayData {
    observableData: IObservableValue<RollupProgressBarData | undefined>;
    observableIcon: IObservableValue<WorkItemTypeColorAndIcon>;
    workItemTypeName: string;
}

function WorkItemRollups(props: {
    compact: boolean;
    item: IClientWorkItem;
    pageContext: IVssPageContext;
    rollupProviders: IWorkItemRollupProvider[];
}): JSX.Element | null {
    const { compact, item, pageContext, rollupProviders } = props;
    if (rollupProviders.length === 0) {
        return null;
    }

    const project = item.getFieldValue<string>(CoreFieldRefNames.TeamProject) ?? "";
    const iconService = pageContext.getService<IWorkItemTypeIconMetadataService>(WorkItemTypeIconMetadataServiceId);

    // hold on to the work item type data
    const colorAndIconMap = React.useRef(new Map<string, WorkItemTypeColorAndIcon>());
    const rollupDisplayData: RollupDisplayData[] = [];
    for (const provider of rollupProviders) {
        const workItemTypeName = provider.getRollupMetric().workItemTypeFilter!;
        const observableData = provider.getRollupData(item.id);
        const cachedColorAndIcon = colorAndIconMap.current.get(workItemTypeName);
        const observableIcon = new ObservableValue<WorkItemTypeColorAndIcon>(
            cachedColorAndIcon ?? {
                color: "",
                icon: "",
                workItemTypeName,
                isDisabled: false
            }
        );

        rollupDisplayData.push({
            observableData,
            observableIcon,
            workItemTypeName
        });
    }

    React.useEffect(() => {
        for (const data of rollupDisplayData) {
            const colorAndIcon: WorkItemTypeColorAndIcon | Promise<WorkItemTypeColorAndIcon> = iconService.getWorkItemIcon(
                project,
                data.workItemTypeName
            );
            if (colorAndIcon instanceof Promise) {
                colorAndIcon.then(resolvedColorAndIcon => {
                    colorAndIconMap.current.set(resolvedColorAndIcon.workItemTypeName, resolvedColorAndIcon);
                    data.observableIcon.value = resolvedColorAndIcon;
                });
            } else {
                colorAndIconMap.current.set(colorAndIcon.workItemTypeName, colorAndIcon);
                data.observableIcon.value = colorAndIcon;
            }
        }
    }, []);

    return (
        <div className="work-item-rollup-bars-container flex-row flex-wrap">
            {rollupDisplayData.map(displayData => {
                const icon = <WorkItemTypeIcon className="margin-right-4" data={displayData.observableIcon} size={TypeIconSize.small} />;
                return (
                    <Observer key={`${item.id}-${displayData.workItemTypeName}`} observableData={displayData.observableData}>
                        {({ observableData }) =>
                            observableData?.totalWorkCount !== 0 ? (
                                <div className="work-item-rollup-bar-container flex-column margin-left-16 margin-top-8 scroll-hidden">
                                    {!compact && (
                                        <WorkItemRollupLabel
                                            className="margin-bottom-4"
                                            icon={icon}
                                            rollupData={observableData}
                                            workItemType={displayData.workItemTypeName}
                                        />
                                    )}
                                    <WorkItemRollupProgressBar icon={compact ? icon : undefined} rollupData={observableData} />
                                </div>
                            ) : null
                        }
                    </Observer>
                );
            })}
        </div>
    );
}

/**
 * Given a list of fields, separate them into coreFields and additionalFields suitable for passing to <Card>.
 * @param fields
 * @returns
 */
export function divyFields(fields: FieldLayout[]): { coreFields: FieldLayoutMap; additionalFields: FieldLayout[] } {
    const coreFields = {} as FieldLayoutMap;
    const additionalFields = [] as FieldLayout[];
    for (const f of fields) {
        if (CoreFields.has(f.referenceName)) {
            coreFields[f.referenceName] = f;
        } else {
            additionalFields.push(f);
        }
    }

    return { coreFields, additionalFields };
}
